import React from "react";

export const INTEGRATION_LOGOS = [
  "SHOPIFY",
  "EBAY",
  "WOOCOMMERCE",
  "AMAZON",
];

export const PAIN_ITEMS = [
  {
    icon: "alert",
    text: (
      <>
        <strong>&ldquo;The listing says 3–5 days, but it takes 12.&rdquo;</strong> By then,
        your customer has already opened a PayPal dispute.
      </>
    ),
  },
  {
    icon: "card",
    text: (
      <>
        <strong>&ldquo;I got charged even after my customer canceled.&rdquo;</strong> Support
        hasn&apos;t replied in 4 days. Hard to trust a platform like that.
      </>
    ),
  },
  {
    icon: "users",
    text: (
      <>
        <strong>&ldquo;Every platform says it&apos;s different.&rdquo;</strong> They all look
        the same. I just need one I can rely on.
      </>
    ),
  },
];

export const HOW_IT_WORKS_STEPS = [
  {
    num: "01",
    title: "Browse pre-vetted catalog",
    badge: "Curated",
    image: "https://images.unsplash.com/photo-1607082348824-0a96f2a4b9da?w=600&q=80",
    desc: "Every product comes from a pre-vetted supplier screened for shipping speed, inventory accuracy, and communication quality.",
    icon: "box",
  },
  {
    num: "02",
    title: "Import to your store",
    badge: "1-click",
    image: "https://images.unsplash.com/photo-1516321318423-f06f85e504b3?w=600&q=80",
    desc: "One-click import to Shopify or eBay. Product data, images, and shipping times sync automatically.",
    icon: "arrow",
  },
  {
    num: "03",
    title: "Orders route automatically",
    badge: "Automated",
    image: "https://images.unsplash.com/photo-1563013544-824ae1b704d3?w=600&q=80",
    desc: "When a customer places an order, it's routed to the supplier automatically — no manual forwarding, no delays.",
    icon: "truck",
  },
  {
    num: "04",
    title: "Supplier ships with verified tracking",
    badge: "Enforced",
    image: "https://images.unsplash.com/photo-1586528116311-ad8dd3c8310d?w=600&q=80",
    desc: "Suppliers must upload verified carrier tracking. Fake or missing tracking leads to removal from the platform.",
    icon: "check",
  },
  {
    num: "05",
    title: "Monitor from your dashboard",
    badge: "Real-time",
    image: "https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=600&q=80",
    desc: "Track every order, supplier score, and fulfillment metric from one clean dashboard — so you know before your customer does.",
    icon: "chart",
  },
  {
    num: "06",
    title: "Scale with confidence",
    badge: "Growth",
    image: "https://images.unsplash.com/photo-1559526324-4b87b5e36e44?w=600&q=80",
    desc: "Run more ads, expand your catalog, and grow revenue — without worrying that fulfillment will break as you scale.",
    icon: "shield",
  },
];

export const FEATURES = [
  {
    title: "Strict supplier vetting",
    desc: "Every supplier goes through a multi-point review covering shipping history, inventory accuracy, cancellation rates, and customer satisfaction.",
    bar: "92%",
    icon: "shield",
  },
  {
    title: "Enforced shipping windows",
    desc: "Suppliers who miss promised shipping windows are flagged automatically. Repeated issues lead to removal.",
    bar: "85%",
    icon: "truck",
  },
  {
    title: "Transparent pricing",
    desc: "No hidden fees or surprise charges. You see your cost, your margin, and our fee before every order. What you see is what you pay.",
    bar: "100%",
    icon: "dollar",
  },
  {
    title: "Live inventory sync",
    desc: "Inventory levels sync in real time between suppliers and your store, so you don't sell out-of-stock products.",
    bar: "78%",
    icon: "search",
  },
  {
    title: "Performance analytics",
    desc: "Track supplier scores, on-time rates, dispute trends, and margins — all in one dashboard, updated daily.",
    bar: "88%",
    icon: "activity",
  },
  {
    title: "Automated tracking sync",
    desc: "Tracking numbers sync to your store automatically, so customers get updates without you lifting a finger.",
    bar: "95%",
    icon: "mail",
  },
];

export const MARGIN_PAIN_ITEMS = [
  {
    text: (
      <>
        <strong>&ldquo;By the time I pay their fee, my margin is gone.&rdquo;</strong> You see
        your cost, margin, and fee before every order.
      </>
    ),
  },
  {
    text: (
      <>
        <strong>&ldquo;Most of their catalog looks good until you run the numbers.&rdquo;</strong>{" "}
        Our products are vetted for margin potential before you list them.
      </>
    ),
  },
  {
    text: (
      <>
        <strong>&ldquo;There&apos;s no margin left after ad spend.&rdquo;</strong> We show you
        recommended pricing so you can protect margin before you scale.
      </>
    ),
  },
];

export const MARGIN_CARDS = [
  {
    label: "No inventory purchases. No warehouse fees.",
    value: "$0 upfront",
    sub: "You only pay when a customer places an order.",
  },
  {
    label: "Built-in cash flow buffer",
    value: "Built-in cash flow buffer",
    sub: "Store revenue comes in before supplier charges are due, giving you a short-term cash flow advantage on every order.",
  },
  {
    label: "See your margin before you list",
    value: "See your margin before you list",
    sub: "Recommended pricing is included on every listing, based on supplier cost and real market data.",
  },
];

export const COMPARE_ROWS = [
  { feature: "Strict supplier vetting before listing", us: "Always", them: "Inconsistent" },
  { feature: "Enforced shipping time windows", us: "Yes", them: "No" },
  { feature: "Transparent, no-surprise billing", us: "Yes", them: "Often unclear" },
  { feature: "Supplier removal for poor performance", us: "Automatic", them: "Manual or inconsistent" },
  { feature: "Real carrier tracking (verified)", us: "Required", them: "Not consistently verified" },
  { feature: "24-hour support response", us: "Guaranteed", them: "4–7 days avg." },
];

export const REVIEWS = [
  {
    text: '"Finally, a platform that doesn\'t overpromise on shipping times. My PayPal disputes dropped 80% in the first 30 days. I was skeptical, but it\'s been solid."',
    transform: "From: drowning in PayPal disputes → To: scaling ads with confidence",
    initials: "JM",
    name: "James M.",
    role: "Shopify seller · $18k/mo revenue",
  },
  {
    text: '"I switched from Spocket after getting burned too many times. The supplier vetting isn\'t a gimmick — they actually remove underperforming suppliers. My store rating went from 4.1 to 4.8."',
    transform: "From: supplier chaos hurting reputation → To: store rating up from 4.1 to 4.8",
    initials: "SR",
    name: "Sara R.",
    role: "eBay seller · $22k/mo revenue",
  },
  {
    text: '"I was spending 3 hours a day answering \'Where is my order?\' emails. Now it\'s under 20 minutes. The automated tracking sync alone cut hours out of support every week."',
    transform: "From: 3 hours a day on support emails → To: under 20 minutes a day",
    initials: "DK",
    name: "David K.",
    role: "Multi-store operator · $35k/mo revenue",
  },
];

export const PRICING_PLANS = [
  {
    plan: "Starter",
    price: "$29.99",
    desc: "For sellers getting started with their first products.",
    featured: false,
    features: [
      "Up to 50 products",
      "1 connected store",
      "Automated order routing",
      "Email support",
    ],
  },
  {
    plan: "Pro",
    price: "$49.99",
    desc: "For high-volume operators who need more control, support, and customization.",
    featured: true,
    badge: "Most popular",
    features: [
      "Everything in Starter",
      "Unlimited store connections",
      "Dedicated account manager",
      "Custom supplier sourcing",
      "API access",
    ],
  },
];

export const FAQ_ITEMS = [
  {
    q: "Will I get charged if my customer asks for a refund?",
    a: "No. If an order is refunded before fulfillment begins, you won't be charged for it. No surprise billing, no charges on canceled orders.",
  },
  {
    q: "How do you vet your suppliers?",
    a: "Every supplier is reviewed for shipping reliability, inventory accuracy, cancellation rates, communication response times, and customer satisfaction before being approved. Suppliers who fail to meet standards are removed from the platform.",
  },
  {
    q: "I've been burned before. How is this different?",
    a: "The difference is enforcement. We don't just vet suppliers once — we monitor performance continuously. Suppliers who ship late, upload fake tracking, or disappear on communication are removed from the platform.",
  },
  {
    q: "Do you work with both Shopify and eBay?",
    a: "Yes. Ecomdropship works with both Shopify and eBay. Growth and Pro support multi-store selling, while Starter supports one connected store.",
  },
  {
    q: "What are the actual fees I'll pay?",
    a: "You pay your monthly subscription plus the supplier product cost when an order is placed. That's it. No transaction fees, no per-order platform charges, and no hidden fees.",
  },
  {
    q: "Can I migrate gradually without disrupting my current store?",
    a: "Yes. Most sellers start by importing 2–5 products, testing shipping speed and margins, then expanding from there. You don't need to migrate everything at once. We support a gradual, low-risk transition.",
  },
  {
    q: "Is there a free trial?",
    a: "Yes — 14 days free. Import products, test shipping, and evaluate margins before committing to anything.",
  },
];

export const RECENT_ORDERS = [
  { id: "#ORD-5821", dest: "New York, NY", status: "Delivered", statusClass: "s-del", time: "2h ago" },
  { id: "#ORD-5820", dest: "Austin, TX", status: "In transit", statusClass: "s-tra", time: "4h ago" },
  { id: "#ORD-5819", dest: "Chicago, IL", status: "Processing", statusClass: "s-pro", time: "6h ago" },
  { id: "#ORD-5818", dest: "Miami, FL", status: "Delivered", statusClass: "s-del", time: "9h ago" },
];
